"use client";

import { Core } from "@faire/design-tokens";
import { localNum } from "@faire/web/common/localization";
import { NumberParser } from "@faire/web/common/NumberParser";
import { Typography } from "@faire/web/slate/Typography";
import * as React from "react";
import styled, { css } from "styled-components";

import { Dropdown, IOption } from "../Dropdown";
import { Input, IProps as IInputProps } from "../Input";

// formatjs makes us choose a number and otherwise defaults to 3,
// I don't think we will ever need more than 10 decimal places,
// so setting here arbitrarily
const MAX_PRECISION = 10;

// JS bails on big numbers. I don't think we have a legitimate reason
// to ever be entering a number larger than this, so setting it as the
// default max to prevent confusion
const MAX_INT = 100000000000000;

export interface IUnitInformation<T extends string = string> {
  /** Called when the user selects a value from the attached unit dropdown */
  onUnitChange: (option: IOption<T>) => void;
  /** Options passed to the dropdown attached to the input */
  unitOptions: IOption<T>[];
  /** Current unit value selected in the Dropdown */
  unitValue?: T;
}

export interface IMeasurementInputProps<T extends string = string>
  extends Omit<IInputProps, "onChange"> {
  /** Value of the input */
  value?: number;
  /** Number of decimal points that should be displayed in the input */
  precision?: number;
  /** Called when the value in the input changes */
  onChange: (value: number | undefined) => void;
  /** Allow value to be undefined, even if min is set */
  optional?: boolean;
  /** Minimum value allowed in the input */
  min?: number;
  /** Maximum value allowed in the input */
  max?: number;
  /** Width of the input component on the screen */
  width?: number;
  /** Set the focus to the input */
  focus?: (options?: FocusOptions) => void;
  /** props passed to the units dropdown attached to the input */
  unitInformation?: IUnitInformation<T>;
  /** The element for the container that the unitInformation dropdown
    should try to stay within. */
  containingElement?: HTMLElement;
  inputRef?: React.RefObject<HTMLInputElement>;
  /** Allows for data to be visible in hotjar recordings */
  "data-hj-allow"?: boolean;
}

/**
 * @trackcomponent
 */
export const MeasurementInput = <T extends string = string>(
  props: IMeasurementInputProps<T>
) => {
  const innerRef = React.useRef<HTMLInputElement>(null);
  const inputRef = props.inputRef ?? innerRef;
  const dataHjAllow = props["data-hj-allow"];

  const localizeNumber = React.useCallback(
    (value: number): string => {
      return (
        localNum(
          value,
          props.precision !== undefined
            ? {
                minimumFractionDigits: props.precision,
                maximumFractionDigits: props.precision,
              }
            : {
                maximumFractionDigits: MAX_PRECISION,
              }
        ) ?? ""
      );
    },
    [props.precision]
  );

  const [value, setValue] = React.useState(
    props.value !== undefined ? localizeNumber(props.value) : ""
  );

  React.useEffect(() => {
    setValue(props.value !== undefined ? localizeNumber(props.value) : "");
  }, [props.value, localizeNumber]);

  const numberParser = new NumberParser();

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    props.onKeyPress?.(e);
    if (e.key === "Enter") {
      inputRef?.current?.blur();
    }
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    props.onBlur?.(e);
    if (value === "") {
      props.onChange(props.optional ? undefined : props.min);
      return;
    }

    let correctedValue = numberParser.parse(value);

    if (Number.isNaN(correctedValue)) {
      setValue(props.value !== undefined ? localizeNumber(props.value) : "");
      return;
    }

    correctedValue = Math.max(props.min ?? -MAX_INT, correctedValue);
    correctedValue = Math.min(props.max ?? MAX_INT, correctedValue);

    setValue(localizeNumber(correctedValue));
    props.onChange(numberParser.parse(localizeNumber(correctedValue)));
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const renderUnitDropdown = () => {
    const { unitInformation } = props;

    return unitInformation ? (
      <NarrowDropdown
        options={unitInformation.unitOptions}
        onSelect={unitInformation.onUnitChange as any}
        value={unitInformation.unitValue}
        disabled={props.disabled}
        containingElement={props.containingElement}
        data-hj-allow={dataHjAllow}
      />
    ) : null;
  };

  // 2021/07/28 Terence Dickson
  // The use of `type="text"` here is done in order to support
  // localized number inputs. The HTML `type="number"` type input
  // does not at the time of writing support any sort of localized
  // number input, and its use is recommended against.
  // See FD-28145,
  // https://stackoverflow.com/questions/13412204/localization-of-input-type-number
  const unitDropdown = renderUnitDropdown();
  const {
    onChange: _onChange,
    suffixNode: _suffixNode,
    value: _value,
    unitInformation: _unitInformation,
    containingElement: _containingElement,
    inputRef: _inputRef,
    "data-hj-allow": __dataHjAllow,
    focus: _focus,
    precision: _precision,
    optional: _optional,
    ...rest
  } = props;

  let suffixNodeComponent;
  if (_suffixNode) {
    if (typeof _suffixNode === "string") {
      suffixNodeComponent = (
        <UnitTypography
          color={props.disabled ? Core.text.disabled : Core.text.default}
          $hasDropdown={!!unitDropdown}
        >
          {_suffixNode}
        </UnitTypography>
      );
    } else {
      suffixNodeComponent = _suffixNode;
    }
  }
  const suffixComponent = suffixNodeComponent || undefined;

  return (
    <StyledInput
      {...rest}
      inputMode="decimal"
      type="text"
      value={value}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      onKeyPress={handleKeyPress}
      // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
      data-test-id={props["data-test-id"] ?? "number-input"}
      ref={inputRef}
      suffixNode={
        _suffixNode ?? unitDropdown ? (
          <>
            {suffixComponent}
            {unitDropdown}
          </>
        ) : null
      }
    />
  );
};

const StyledInput = styled(Input)`
  ${(props) =>
    props.width !== undefined
      ? css`
          width: ${props.width}px;
        `
      : ""};
`;

const UnitTypography = styled(Typography)<{ $hasDropdown: boolean }>`
  position: relative;
  ${(props) => (props.$hasDropdown ? "right: 8px" : "")};
`;

const NarrowDropdown = styled(Dropdown)<{ disabled?: boolean }>`
  width: 80px;
  margin-right: -16px;
  & div[role="button"] {
    height: 40px;
    border: 0;
    border-left: 1px solid ${Core.border.muted};
    &:active,
    &:focus,
    &:focus-within {
      border-left: 1px solid
        ${(props) => (props.disabled ? Core.border.muted : Core.border.default)};
    }
  }
`;
